import { useAuth } from '@hub/auth';
import { m } from '@hub/messages';
import { anonymousEntry, RouterPreloadSync, RouterProvider, router, warmRoute } from '@hub/router';
import { useEvaluationContext, useFeatureFlagStore } from '@platform/feature-flags';
import { LoadingOverlay } from '@platform/ui';
import React from 'react';
import { queryClient } from './queryClient';

export function AuthedRouterProvider() {
  const auth = useAuth();
  const featureFlags = useFeatureFlagStore();
  const [flagContext] = useEvaluationContext();

  const flagsHaveUser = auth.status === 'authenticated' ? flagContext.userId === auth.user.userHandle : true;

  // Background preload of the chunk an unauthed visitor is about to be redirected onto, so the
  // _authenticated → sign-in redirect is seamless. Render is intentionally NOT gated on this:
  // public routes must stay reachable without that bundle.
  React.useEffect(() => {
    if (auth.status === 'loading' || auth.status === 'authenticated') {
      return;
    }
    warmRoute(anonymousEntry().id);
  }, [auth]);

  // _authenticated.beforeLoad throws loginRedirect before any layout mounts,
  // so the wrong-layout chrome flash the previous gate guarded against can't
  // occur — we only need to wait for the initial auth resolve.
  //
  // This early return is what makes `RouterContext.auth` settled. See `SettledRouterAuth`.
  if (auth.status === 'loading' || !flagsHaveUser) {
    return (
      <div className="min-h-screen bg-background">
        <LoadingOverlay open label={m.commons_loading_label()} labelHidden />
      </div>
    );
  }

  return (
    <>
      <RouterPreloadSync />
      <RouterProvider router={router} context={{ auth, queryClient, featureFlags }} />
    </>
  );
}
